import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Bridge Honeybalm custom add-to-cart scripts to Horizon's cart drawer.
 * HB sections call `HBCart.notifyCartUpdated(cart)` (or dispatch `cart:updated`)
 * after a successful /cart/add.js call; this turns that into a Shopify
 * CartLinesUpdateEvent so the drawer refreshes and auto-opens.
 */

/**
 * @param {object} [ajaxCart] - Cart JSON from /cart.js
 * @param {{ action?: 'add' | 'update' | 'remove', lines?: Array<{ merchandiseId?: string, id?: string, quantity?: number }> }} [options]
 */
export function notifyCartUpdated(ajaxCart, options = {}) {
  const action = options.action || 'add';
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'product',
      lines: options.lines || [],
      promise: deferred.promise,
    })
  );

  const resolve = (cart) => {
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        source: 'hb-cart',
        didError: false,
      },
    });
  };

  if (ajaxCart) {
    resolve(ajaxCart);
    return;
  }

  const cartUrl = window.Theme?.routes?.cart_url || '/cart';
  fetch(`${cartUrl}.js`)
    .then((response) => response.json())
    .then(resolve)
    .catch((error) => deferred.reject(error));
}

/**
 * Opens the cart drawer if present (cart page has no drawer).
 */
export function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  }
}

const early = window.HBCart;
const queued = Array.isArray(early?._queue) ? early._queue.slice() : [];

window.HBCart = {
  notifyCartUpdated,
  openCartDrawer,
};

document.addEventListener('cart:updated', (event) => {
  const detail = event instanceof CustomEvent ? event.detail : null;
  notifyCartUpdated(detail?.cart, {
    action: detail?.action || 'add',
    lines: detail?.lines,
  });
});

for (const item of queued) {
  notifyCartUpdated(item.cart, item.options);
}
